/**
 * ============================================================================
 * KbVersionsTimeline — Linha do tempo de versões de uma página.
 * ----------------------------------------------------------------------------
 * CHANGELOG:
 *   RELEASE: 2026-07-07 v1.0.0 - F1-Fix.
 * ============================================================================
 */
import { GitBranch } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useKbPageVersions } from "@/hooks/kb/useKbEditorial";

interface Props { pageId?: string | null }

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline"> = {
  published: "default",
  approved: "secondary",
  in_review: "outline",
  draft: "outline",
};

export function KbVersionsTimeline({ pageId }: Props) {
  const { data, isLoading } = useKbPageVersions(pageId);

  if (isLoading) return <Skeleton className="h-24 w-full" />;
  if (!data || data.length === 0) {
    return <div className="text-sm text-muted-foreground">Nenhuma versão registrada.</div>;
  }

  return (
    <div className="space-y-2">
      {data.map((v: any) => (
        <div key={v.id} className="flex items-start gap-2 text-sm">
          <GitBranch className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium">v{v.version_number}</span>
              <Badge variant={STATUS_VARIANT[v.status] ?? "outline"} className="text-[10px]">{v.status}</Badge>
              {typeof v.source_confidence === "number" && (
                <span className="text-xs text-muted-foreground">conf: {Number(v.source_confidence).toFixed(2)}</span>
              )}
            </div>
            {v.review_comment && (
              <div className="text-xs text-muted-foreground truncate">{v.review_comment}</div>
            )}
            <div className="text-[10px] text-muted-foreground">
              {v.published_at ? `publicado ${new Date(v.published_at).toLocaleString()}` :
               v.reviewed_at ? `revisado ${new Date(v.reviewed_at).toLocaleString()}` :
               `criado ${new Date(v.created_at).toLocaleString()}`}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
